import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { Box, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { PageLayout } from '@ui/components/PageLayout';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { hasPermission } from '@application/helpers/permission-helper';
import { getAllUsageManualsAsync, getLatestUsageManualAsync, replaceUsageManualFileAsync, uploadUsageManualAsync } from '@application/features/usageManual/usageManualThunk';
import { APIStatus } from '@application/enums/api-status';
import UsageManualAdminView from './components/UsageManualAdminView';
import UsageManualReaderView from './components/UsageManualReaderView';
import UploadUsageManualDialog from './UploadUsageManualDialog';
import { HugeIconsUpload04 } from '@ui/assets/custom-icons/upload-4';
import { HugeIconsDownload04 } from '@ui/assets/custom-icons/download-04';
import { Book1 } from 'iconsax-react';
const groupDtosToVersions = (dtos) => {
    const map = new Map();
    for (const dto of dtos) {
        if (!map.has(dto.versionLabel)) {
            map.set(dto.versionLabel, {
                versionLabel: dto.versionLabel,
                version: dto.versionLabel,
                majorVersion: dto.majorVersion,
                minorVersion: dto.minorVersion,
                patchVersion: dto.patchVersion,
                documents: [],
                fileSizeMb: Number(dto.fileSize) || 0,
                updatedAt: dto.createdDate,
                updatedBy: dto.createdByName || dto.createdBy,
                isLatest: dto.isLatest
            });
        }
        const version = map.get(dto.versionLabel);
        version.documents.push({
            id: dto.id,
            language: dto.language || 'en',
            documentUrl: dto.url,
            documentName: dto.fileName
        });
        // A version is latest if any of its records is latest
        if (dto.isLatest)
            version.isLatest = true;
    }
    return Array.from(map.values());
};
const UsageManualPage = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { currentUser } = useAppSelector((state) => state.auth);
    const { manuals, latest, uploadStatus } = useAppSelector((state) => state.usageManual);
    const canManageManuals = hasPermission(currentUser?.permissions ?? [], 'Permissions.UsageManuals.Read');
    const isTransportManager = Array.isArray(currentUser?.role) && currentUser?.role.includes('TransportManager');
    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
    const [uploadDialogMode, setUploadDialogMode] = useState('upload');
    const [lockedLanguage, setLockedLanguage] = useState(undefined);
    // Tracks which specific record is being edited (for replace-file operation)
    const [editingRecordId, setEditingRecordId] = useState(undefined);
    const [existingDocument, setExistingDocument] = useState(undefined);
    // Tracks version to target when adding a missing language
    const [targetVersion, setTargetVersion] = useState(undefined);
    const openUploadDialog = (mode, options) => {
        setUploadDialogMode(mode);
        setLockedLanguage(options?.lockedLanguage);
        setEditingRecordId(options?.editingRecordId);
        setExistingDocument(options?.existingDocument);
        setTargetVersion(options?.targetVersion);
        setUploadDialogOpen(true);
    };
    const closeUploadDialog = () => {
        setUploadDialogOpen(false);
        setUploadDialogMode('upload');
        setLockedLanguage(undefined);
        setEditingRecordId(undefined);
        setExistingDocument(undefined);
        setTargetVersion(undefined);
    };
    useEffect(() => {
        if (canManageManuals) {
            dispatch(getAllUsageManualsAsync());
        }
        else {
            dispatch(getLatestUsageManualAsync());
        }
    }, [canManageManuals, dispatch]);
    const versions = useMemo(() => groupDtosToVersions(manuals), [manuals]);
    const latestVersion = useMemo(() => versions.find((v) => v.isLatest) ??
        (latest
            ? groupDtosToVersions([latest])[0]
            : undefined), [versions, latest]);
    const [selectedVersionLabel, setSelectedVersionLabel] = useState('');
    useEffect(() => {
        if (versions.length > 0 && !selectedVersionLabel) {
            setSelectedVersionLabel(versions.find((v) => v.isLatest)?.versionLabel ?? versions[0]?.versionLabel ?? '');
        }
    }, [versions]);
    const handleUpload = async (files) => {
        if (uploadDialogMode === 'edit' && editingRecordId) {
            const result = await dispatch(replaceUsageManualFileAsync({ id: editingRecordId, file: files[0].file }));
            if (replaceUsageManualFileAsync.fulfilled.match(result)) {
                toast.success(String(t('help.usageManual.uploadSuccess')));
            }
            else {
                toast.error(String(t('help.usageManual.uploadFailed')));
            }
        }
        else {
            let currentTargetVersion = targetVersion;
            let firstVersionLabel = '';
            let allSuccess = true;
            for (const { file, language } of files) {
                const result = await dispatch(uploadUsageManualAsync({ file, language, targetVersion: currentTargetVersion }));
                if (uploadUsageManualAsync.fulfilled.match(result)) {
                    const uploaded = result.payload;
                    if (!firstVersionLabel)
                        firstVersionLabel = uploaded.versionLabel;
                    if (!currentTargetVersion) {
                        currentTargetVersion = {
                            major: uploaded.majorVersion,
                            minor: uploaded.minorVersion,
                            patch: uploaded.patchVersion
                        };
                    }
                }
                else {
                    allSuccess = false;
                    toast.error(String(t('help.usageManual.uploadFailed')));
                }
            }
            if (firstVersionLabel)
                setSelectedVersionLabel(firstVersionLabel);
            if (allSuccess)
                toast.success(String(t('help.usageManual.uploadSuccess')));
        }
    };
    const handleDownloadLatest = () => {
        const firstDoc = latestVersion?.documents[0];
        if (!firstDoc?.documentUrl)
            return;
        const url = firstDoc.documentUrl.includes('?')
            ? `${firstDoc.documentUrl}&download=true`
            : `${firstDoc.documentUrl}?download=true`;
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', firstDoc.documentName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };
    const leftIcon = _jsx(Book1, { variant: 'Outline' });
    const rightContent = canManageManuals ? (_jsx(Button, { variant: 'contained', color: 'primary', size: 'large', startIcon: _jsx(HugeIconsUpload04, {}), onClick: () => openUploadDialog('upload'), children: t('help.usageManual.uploadNew') })) : (_jsx(Button, { variant: 'contained', color: 'primary', size: 'large', startIcon: _jsx(HugeIconsDownload04, {}), onClick: handleDownloadLatest, children: t('help.usageManual.downloadPdf') }));
    return (_jsxs(_Fragment, { children: [_jsx(PageLayout, { title: canManageManuals
                    ? String(t('help.usageManual.adminTitle'))
                    : String(t('help.usageManual.userTitle')), secondTitle: canManageManuals
                    ? String(t('help.usageManual.adminSubtitle'))
                    : String(t('help.usageManual.userSubtitle')), showLeftIcon: true, leftIcon: leftIcon, rightContent: rightContent, sx: { pb: 3 }, children: _jsx(Box, { sx: { py: 2 }, children: canManageManuals ? (_jsx(UsageManualAdminView, { manuals: versions, selectedVersionLabel: selectedVersionLabel, onSelectVersion: setSelectedVersionLabel, onUploadClick: () => openUploadDialog('upload'), onEditClick: (recordId, language) => {
                            const doc = versions
                                .flatMap((v) => v.documents)
                                .find((d) => d.id === recordId);
                            openUploadDialog('edit', {
                                editingRecordId: recordId,
                                lockedLanguage: language,
                                existingDocument: doc ? { name: doc.documentName, url: doc.documentUrl } : undefined
                            });
                        }, onAddLanguageClick: (language, version) => openUploadDialog('upload', { lockedLanguage: language, targetVersion: version }) })) : (latestVersion && _jsx(UsageManualReaderView, { manual: latestVersion })) }) }), canManageManuals && (_jsx(UploadUsageManualDialog, { open: uploadDialogOpen, onClose: closeUploadDialog, onUpload: handleUpload, uploading: uploadStatus === APIStatus.PENDING, mode: uploadDialogMode, lockedLanguage: lockedLanguage, existingDocument: existingDocument, displayVersion: targetVersion
                    ? `${targetVersion.major}.${targetVersion.minor}.${targetVersion.patch}`
                    : undefined }))] }));
};
export default UsageManualPage;
